import { supabase } from "@/integrations/supabase/client";

export type ActivitySource = "view" | "read" | "play";

export type UserActivityEvent = {
  id: string;
  source: ActivitySource;
  userId: number;
  bookId: number;
  bookTitle?: string;
  chapterId?: number | null;
  minutes?: number;
  completed?: boolean;
  endedAt?: string | null;
  platform?: string | null;
  appVersion?: string | null;
  deviceModel?: string | null;
  createdAt: string; // ISO
};

type FetchParams = {
  userId: number;
  limit?: number;
  fromDate?: string;
  toDate?: string;
  cursor?: string | null;
};

export async function fetchUserActivity({
  userId,
  limit = 50,
  fromDate,
  toDate,
  cursor,
}: FetchParams): Promise<UserActivityEvent[]> {
  const perTableLimit = Math.max(20, Math.ceil(limit * 1.2));

  const addRange = (q: any, tsCol: string) => {
    let query = q.eq("user_id", userId);
    if (fromDate) query = query.gte(tsCol, fromDate);
    if (toDate) query = query.lte(tsCol, toDate);
    if (cursor) query = query.lt(tsCol, cursor);
    return query;
  };

  const [viewsRes, readsRes, playsRes] = await Promise.all([
    addRange(
      supabase
        .from("book_views")
        .select(
          "id, book_id, user_id, viewed_at, platform, app_version, device_model"
        )
        .order("viewed_at", { ascending: false })
        .limit(perTableLimit),
      "viewed_at"
    ),
    addRange(
      supabase
        .from("book_reads")
        .select(
          "id, book_id, user_id, started_at, ended_at, active_seconds, completed, platform, app_version, device_model, chapter_id"
        )
        .order("started_at", { ascending: false })
        .limit(perTableLimit),
      "started_at"
    ),
    addRange(
      supabase
        .from("book_audio_plays")
        .select(
          "id, book_id, user_id, started_at, play_seconds, platform, app_version, device_model, chapter_id"
        )
        .order("started_at", { ascending: false })
        .limit(perTableLimit),
      "started_at"
    ),
  ]);

  const views = (viewsRes.data || []).map((v: any) => ({
    id: `view:${v.id}`,
    source: "view" as const,
    userId: v.user_id,
    bookId: v.book_id,
    createdAt: v.viewed_at,
    platform: v.platform,
    appVersion: v.app_version,
    deviceModel: v.device_model,
  }));

  const reads = (readsRes.data || []).map((r: any) => ({
    id: `read:${r.id}`,
    source: "read" as const,
    userId: r.user_id,
    bookId: r.book_id,
    chapterId: r.chapter_id,
    minutes: r.active_seconds ? Math.round(r.active_seconds / 60) : undefined,
    completed: !!r.completed,
    endedAt: r.ended_at || null,
    createdAt: r.started_at,
    platform: r.platform,
    appVersion: r.app_version,
    deviceModel: r.device_model,
  }));

  const plays = (playsRes.data || []).map((p: any) => ({
    id: `play:${p.id}`,
    source: "play" as const,
    userId: p.user_id,
    bookId: p.book_id,
    chapterId: p.chapter_id,
    minutes: p.play_seconds ? Math.round(p.play_seconds / 60) : undefined,
    createdAt: p.started_at,
    platform: p.platform,
    appVersion: p.app_version,
    deviceModel: p.device_model,
  }));

  return [...views, ...reads, ...plays]
    .sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, limit);
}


